// Brand-declared auto discount catalog (#35626).
//
// Branch's auto discounts (Community Drive, paperless, employee, multi-policy/multiline, …) are
// hard-coded across staff-fe, account-fe, the Simple Quote PDF and the auto docs. A brand whose
// auto product is rated off a different plan (CFM, via ClarionDoor) offers a different set, so it
// declares that set in its brand config (`config.autoDiscounts`, brand-config-be AND
// brand-config-fe) as ids from this catalog. Every surface that shows auto discounts then renders
// exactly that list — and none of Branch's — resolved against the offer/policy by
// `resolveAutoDiscounts`. A brand without `autoDiscounts` keeps Branch's legacy discount handling.
//
// Lives in @ourbranch/lookups because it is the one package that the FE apps, both brand-config
// packages and every backend consumer (docs-renderer, offer-pdf-document, renewal comparison)
// already depend on, so the labels and applied-rules can't drift between surfaces.

export const AutoDiscountId = {
  MultiCar: 'multiCar',
  Homeowner: 'homeowner',
  PaidInFull: 'paidInFull',
  Transfer: 'transfer',
  Renewal: 'renewal',
  ClaimsFree: 'claimsFree',
  ViolationFree: 'violationFree'
} as const;

export type AutoDiscountId = (typeof AutoDiscountId)[keyof typeof AutoDiscountId];

type AutoDiscountDefinition = {
  label: string;
  description?: string;
  // ClarionDoor savings row (option.autoCoverages / segment coverages `type`) whose amount is the
  // discount's premium effect. Absent for discounts the rater applies from an input without
  // returning a savings row — those are applied from the same input instead.
  savingsCoverageType?: string;
};

export const autoDiscountDefinitions: Record<AutoDiscountId, AutoDiscountDefinition> = {
  // The CFM rater sends `num_vehicles = cars.length`; trailers are a separate array and not counted.
  [AutoDiscountId.MultiCar]: {
    label: 'Multi Car',
    description: 'More than one car is insured on the policy.'
  },
  [AutoDiscountId.Homeowner]: {
    label: 'Homeowner',
    description: 'The primary named insured owns their residence.',
    savingsCoverageType: 'homeowner_savings'
  },
  [AutoDiscountId.PaidInFull]: {
    label: 'Paid in Full',
    description: 'The policy premium is paid in full rather than in installments.',
    savingsCoverageType: 'paid_in_full_savings'
  },
  [AutoDiscountId.Transfer]: {
    label: 'Transfer',
    savingsCoverageType: 'transfer_savings'
  },
  [AutoDiscountId.Renewal]: {
    label: 'Renewal',
    savingsCoverageType: 'renewal_savings'
  },
  // Per-driver inputs (`claims_freeYN` / `violation_freeYN`) derived by CfmAutoPostDataRules.
  [AutoDiscountId.ClaimsFree]: {
    label: 'Claims Free',
    description: 'A rated driver has had no at-fault claims in the last 3 years.'
  },
  [AutoDiscountId.ViolationFree]: {
    label: 'Violation Free',
    description: 'A rated driver has had no chargeable violations.'
  }
};

// CFM auto (ClarionDoor PPA_VEHICLES_2), in display order.
export const CFM_AUTO_DISCOUNTS: AutoDiscountId[] = [
  AutoDiscountId.MultiCar,
  AutoDiscountId.Homeowner,
  AutoDiscountId.PaidInFull,
  AutoDiscountId.Transfer,
  AutoDiscountId.Renewal,
  AutoDiscountId.ClaimsFree,
  AutoDiscountId.ViolationFree
];

type AutoDiscountCoverage = { type?: string | null; amount?: number | null };
type AutoDiscountDriver = {
  excludeDriver?: boolean | null;
  autoClaimsFree?: boolean | null;
  autoViolationFree?: boolean | null;
};

export type ResolvedAutoDiscount = {
  id: AutoDiscountId;
  label: string;
  description?: string;
  isApplied: boolean;
  // Summed savings-row amount; null for a discount that has no savings row.
  amount: number | null;
};

/**
 * Resolves a brand's auto discount list against an offer option or policy segment: whether each
 * discount applies and, when the rater returns a savings row for it, the summed amount across
 * every iteration (one row per rated vehicle).
 */
export function resolveAutoDiscounts({
  discountIds,
  cars,
  drivers,
  autoCoverages,
  importedDiscountIds
}: {
  discountIds: readonly AutoDiscountId[];
  cars?: readonly unknown[] | null;
  drivers?: readonly (AutoDiscountDriver | null | undefined)[] | null;
  autoCoverages?: readonly (AutoDiscountCoverage | null | undefined)[] | null;
  /**
   * The discounts the SOURCE system applied on a migrated term (`segment.auto.importedDiscounts`,
   * written by the importer from GO's per-risk Discount rows, #36344). They stand in for the
   * ClarionDoor savings rows only while the term has never been rated here: a rated segment always
   * carries its savings rows (at 0 when a discount does not apply), and those are authoritative — a
   * renewal copies `auto` from the imported term, so a stale imported flag must never outrank them.
   */
  importedDiscountIds?: readonly string[] | null;
}): ResolvedAutoDiscount[] {
  const ratedDrivers = (drivers ?? []).filter(
    (driver): driver is AutoDiscountDriver => Boolean(driver) && !driver?.excludeDriver
  );
  const coverages = autoCoverages ?? [];
  const hasSavingsRows = coverages.some((coverage) => String(coverage?.type ?? '').endsWith('_savings'));
  const imported = new Set(importedDiscountIds ?? []);

  return discountIds.map((id) => {
    const { label, description, savingsCoverageType } = autoDiscountDefinitions[id];

    if (savingsCoverageType) {
      if (!hasSavingsRows && imported.size > 0) {
        return { id, label, description, isApplied: imported.has(id), amount: null };
      }
      const amount = coverages
        .filter((coverage) => coverage?.type === savingsCoverageType)
        .reduce((total, coverage) => total + (Number(coverage?.amount) || 0), 0);
      return { id, label, description, isApplied: amount > 0, amount };
    }

    let isApplied = false;
    if (id === AutoDiscountId.MultiCar) {
      isApplied = (cars ?? []).length > 1;
    } else if (id === AutoDiscountId.ClaimsFree) {
      isApplied = ratedDrivers.some((driver) => driver.autoClaimsFree === true);
    } else if (id === AutoDiscountId.ViolationFree) {
      isApplied = ratedDrivers.some((driver) => driver.autoViolationFree === true);
    }
    return { id, label, description, isApplied, amount: null };
  });
}
